import { useRef, useCallback } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';
import styles from './InputOTP.module.scss';

export interface InputOTPProps {
  /** Current OTP value */
  value: string;

  /** Called when value changes */
  onChange: (value: string) => void;

  /** Called when all digits are entered */
  onComplete?: (value: string) => void;

  /** Number of OTP digits (4-10, default 6) */
  length?: number;

  /** Disabled state */
  disabled?: boolean;

  /** Additional CSS classes */
  className?: string;

  /** Auto focus first input on mount */
  autoFocus?: boolean;
}

export const InputOTP = ({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  className = '',
  autoFocus = false,
}: InputOTPProps) => {
  // Clamp length between 4 and 10
  const clampedLength = Math.min(10, Math.max(4, length));

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Convert value string to array of digits
  const digits = value.split('').slice(0, clampedLength);

  // Pad with empty strings to match length
  while (digits.length < clampedLength) {
    digits.push('');
  }

  const focusInput = useCallback((index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  const handleChange = useCallback(
    (index: number, digit: string) => {
      // Only allow single digit
      const sanitized = digit.replace(/\D/g, '').slice(-1);

      // Build new value
      const newDigits = [...digits];
      newDigits[index] = sanitized;
      const newValue = newDigits.join('');

      onChange(newValue);

      // Auto-advance to next input if digit was entered
      if (sanitized && index < clampedLength - 1) {
        focusInput(index + 1);
      }

      // Check if complete
      if (newValue.length === clampedLength && onComplete) {
        onComplete(newValue);
      }
    },
    [digits, onChange, onComplete, clampedLength, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!digits[index] && index > 0) {
          // If current input is empty, move to previous and clear it
          e.preventDefault();
          const newDigits = [...digits];
          newDigits[index - 1] = '';
          onChange(newDigits.join(''));
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < clampedLength - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }
    },
    [digits, onChange, clampedLength, focusInput]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const sanitized = pastedData.replace(/\D/g, '').slice(0, clampedLength);

      if (sanitized) {
        onChange(sanitized);

        // Focus the next empty input or the last one
        const nextIndex = Math.min(sanitized.length, clampedLength - 1);
        focusInput(nextIndex);

        // Check if complete
        if (sanitized.length === clampedLength && onComplete) {
          onComplete(sanitized);
        }
      }
    },
    [onChange, onComplete, clampedLength, focusInput]
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const containerClassName = [styles.container, disabled ? styles.disabled : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassName}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- autoFocus is intentional for OTP inputs to improve UX
          autoFocus={autoFocus && index === 0}
          className={styles.input}
          aria-label={`Digit ${index + 1} of ${clampedLength}`}
        />
      ))}
    </div>
  );
};
